import {
  TABLE_MANAGEMENT_VOID_ITEM_REPORT_PAGE_REQUEST,
  TABLE_MANAGEMENT_VOID_ITEM_REPORT_LIST_REQUEST,
  TABLE_MANAGEMENT_VOID_ITEM_REPORT_CHANGE_FILTER,
  TABLE_MANAGEMENT_VOID_ITEM_REPORT_EXPORT,
} from 'actions/tableManagement/constants';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { initialize, change } from 'redux-form';

import * as apiOutlets from 'api/outlets';
import * as actTableManagement from 'actions/tableManagement/voidItem';
import * as apiProfile from 'api/profile';
import * as act from 'actions';
import * as actNotif from 'actions/notifications';
import * as apiTableManagement from 'api/tableManagement';
import { showPageLoadingSpinner } from 'actions/loadingSpinner';

import { getFilteredDate } from 'api/get-filtered-date';
import moment from 'moment';
import { DEFAULT_END_HOUR, DEFAULT_START_HOUR } from 'utils/hourFilter';
import fileDownload from 'react-file-download';
import { tableVoidItemReportStart } from 'clevertap/tableManagement';

export const getBusinessId = state => state.moka.user.business.id;
export const getOutletId = state => state.moka.user.outlet_id[0];

const PAGE_SIZE = 50;
const STATUSES = {
  0: 'COMPLETE,CANCELLED',
  1: 'COMPLETE',
  2: 'CANCELLED',
};

function* fillSearch(profile, startDate, endDate) {
  let assignedOutletIds = [];
  if (profile.outlet_id.length > 1) {
    assignedOutletIds = [profile.outlet_id[0]];
  } else {
    assignedOutletIds = profile.outlet_id;
  }

  const allDay = moment(endDate).format('HH:mm') === '23:59';
  const startHour = !allDay
    ? moment(startDate).format('HH:mm')
    : DEFAULT_START_HOUR;

  const endHour = !allDay ? moment(endDate).format('HH:mm') : DEFAULT_END_HOUR;
  const data = {
    outletIds: assignedOutletIds,
    date: { startDate, endDate, startHour, endHour, allDay },
  };
  yield put(initialize('search', data));
}

export function* onPageRequest() {
  try {
    yield put(act.showProgressBar());
    yield put(showPageLoadingSpinner(true));
    yield put(actTableManagement.setReports([]));
    yield put(actTableManagement.setReportSummary([]));
    yield put(actTableManagement.setReportDetail(null));

    const filteredDate = yield call(getFilteredDate);
    const startDate = filteredDate.data.start_date;
    const endDate = filteredDate.data.end_date;
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD HH:mm:ss');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD HH:mm:ss');

    const request = {
      payload: {
        page: 1,
        startDate,
        endDate,
        previousReports: null,
      },
    };

    yield call(onListRequest, request);

    const response = yield call(apiOutlets.outlets);
    yield put(actTableManagement.setOutlets(response.data));
    const resProfile = yield call(apiProfile.getProfile);
    yield call(
      fillSearch,
      resProfile.data,
      formattedStartDate,
      formattedEndDate
    );

    const outletId = yield select(getOutletId);
    yield call(tableVoidItemReportStart, {
      outletId,
      startDate,
      endDate,
    });
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
    yield put(act.hideProgressBar());
  }
}

export function* onChangeFilter(request) {
  try {
    const {
      payload: { outletIds, startDate, endDate },
    } = request;
    yield put(act.showProgressBar());
    yield put(showPageLoadingSpinner(true));

    yield put(actTableManagement.setReports([]));
    yield put(actTableManagement.setReportSummary([]));
    yield put(actTableManagement.setReportDetail(null));
    yield put(change('search', 'outletIds', outletIds));

    const formattedStart = moment(startDate).format();
    const formattedEnd = moment(endDate).format();

    request.payload.page = 1;
    request.payload.startDate = formattedStart;
    request.payload.endDate = formattedEnd;
    request.previousReports = null;

    yield call(apiOutlets.patchLogin, { id: outletIds });
    const response = yield call(apiOutlets.outlets);
    yield put(actTableManagement.setOutlets(response.data));
    const resProfile = yield call(apiProfile.getProfile);
    yield call(fillSearch, resProfile.data, formattedStart, formattedEnd);

    yield call(onListRequest, request);
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* onListRequest(request) {
  const { page } = request.payload;
  try {
    yield put(act.showProgressBar());

    const summary = yield call(getVoidItemSummary, request);
    const reports = yield call(getVoidItemReports, request);

    yield put(actTableManagement.setReports(reports));
    yield put(actTableManagement.setReportSummary(summary));
    yield put(actTableManagement.setReportsTotalPage(summary.total_pages));
    yield put(actTableManagement.setReportsCurrentPage(page));
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onExportReport(request) {
  const {
    payload: { startDate, endDate },
  } = request;
  try {
    yield put(showPageLoadingSpinner(true));
    yield put(act.showProgressBar());

    const formattedStart = moment(startDate).format();
    const formattedEnd = moment(endDate).format();
    request.payload.startDate = formattedStart;
    request.payload.endDate = formattedEnd;

    const responseExport = yield call(exportVoidItemReports, request);
    const csv = responseExport.data;
    const fileName = responseExport.headers.filename;
    yield call(fileDownload, csv, fileName);
  } catch (error) {
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}
function* getVoidItemSummary(request) {
  const {
    payload: { startDate, endDate, page },
  } = request;
  const query = {
    size: PAGE_SIZE,
    page,
    startDate,
    endDate,
    statuses: STATUSES[0],
  };
  const outletId = yield select(getOutletId);
  const response = yield call(
    apiTableManagement.getTableReportVoidItemSummary,
    outletId,
    query
  );
  const { totalVoidItems, totalVoidPrice } = response.data;
  const summary = {
    totalVoidItems,
    totalVoidPrice,
    total_pages: Math.ceil(totalVoidItems / PAGE_SIZE),
  };
  return summary;
}

function* getVoidItemReports(request) {
  const {
    payload: { startDate, endDate, page, previousReports },
  } = request;
  const query = {
    size: PAGE_SIZE,
    page,
    startDate,
    endDate,
    statuses: STATUSES[0],
  };
  const outletId = yield select(getOutletId);
  const response = yield call(
    apiTableManagement.getTableReportVoidItems,
    outletId,
    query
  );

  if (previousReports) {
    return previousReports.concat(response.data);
  }
  return response.data;
}

function* exportVoidItemReports(request) {
  const {
    payload: { startDate, endDate, outletName },
  } = request;
  const query = {
    startDate,
    endDate,
    outletName,
    statuses: STATUSES[0],
  };
  const outletId = yield select(getOutletId);

  return yield call(
    apiTableManagement.getTableReportVoidExport,
    outletId,
    query
  );
}

export default function* tableManagementReportSaga() {
  yield takeLatest(
    TABLE_MANAGEMENT_VOID_ITEM_REPORT_PAGE_REQUEST,
    onPageRequest
  );

  yield takeLatest(
    TABLE_MANAGEMENT_VOID_ITEM_REPORT_LIST_REQUEST,
    onListRequest
  );

  yield takeLatest(
    TABLE_MANAGEMENT_VOID_ITEM_REPORT_CHANGE_FILTER,
    onChangeFilter
  );

  yield takeLatest(TABLE_MANAGEMENT_VOID_ITEM_REPORT_EXPORT, onExportReport);
}
